// ============================================================================
// ModuleSidebar.js — Barre latérale GÉNÉRIQUE des modules Aramis
// ============================================================================
// Généralisation (22/08/2026) de la barre latérale RH validée par Pierre :
// « un bandeau du haut pour naviguer entre les modules, puis une fois dedans,
// un bandeau latéral avec tous les sous-modules du module — et les sous-
// modules des sous-modules s'il y en a ». Style Réglages iOS : verre dépoli,
// sections colorées, pastille d'icône colorée par sous-module, page active
// teintée ; sur mobile (<1000px) un bouton ouvre un panneau coulissant.
//
// Source unique : le module tel que renvoyé par buildNavModules (navConstants)
//   { key, label, color, accueil:{route,label,icon,children?}, items:[{route,
//     label, icon, description?, group?, color?, show?, children?:[{route,label,icon}]}] }
// Les couleurs viennent de `item.color` (curation dans navConstants) ; à
// défaut, une palette Aramis est cyclée. Les `children` sont rendus indentés
// sous leur parent (sous-sous-modules).
// ============================================================================

import { useState, useEffect } from "react";
import { MODULE_VIEWS, NAV_COLORS, navTexte, compteEntree, pageServie } from "./navConstants";
import { CadenasAdmin } from "./cadenasAdmin";
import { Icone, Tuile } from "./icones";
import { useCoquille } from "./responsive";
import { alpha } from "./alpha";
import PastilleCompteur from "./PastilleCompteur";

// Repli cyclé pour une entrée sans `color` : teintes de la charte seulement (lot
// L3), lues dans NAV_COLORS — aucune teinte recopiée ici.
const PALETTE = [NAV_COLORS.rh, NAV_COLORS.athos, NAV_COLORS.mutuelles, NAV_COLORS.gouv, NAV_COLORS.exploration,
  NAV_COLORS.events, NAV_COLORS.hygie, NAV_COLORS.pilotage, NAV_COLORS.argos];
const NAVY = NAV_COLORS.pilotage;

// Module courant pour une vue : appartenance par la liste de vues du module
// (MODULE_VIEWS, qui inclut les sous-pages hors menu : détails, drawers…),
// sinon par les routes déclarées (accueil, items, children).
export function moduleForView(modules, view) {
  if (!view) return null;
  for (const m of modules || []) {
    if (m.show === false) continue;
    if ((MODULE_VIEWS[m.key] || []).includes(view)) return m;
    if (m.accueil?.route === view) return m;
    if ((m.accueil?.children || []).some(c => c.route === view)) return m;
    for (const it of m.items || []) {
      if (it.route === view) return m;
      if ((it.children || []).some(c => c.route === view)) return m;
    }
  }
  return null;
}

// Publie le module de la vue sur <html data-module="…"> : theme.css en dérive
// --m, --m-ink, --m-on, --m-soft (lot L3). Un composant plutôt qu'un hook posé
// dans App : le rendu d'App a des retours anticipés, et un hook placé après eux
// changerait de rang d'un rendu à l'autre.
export function AttributModuleHtml({ cle }) {
  useEffect(() => {
    const html = document.documentElement;
    if (cle) html.dataset.module = cle; else delete html.dataset.module;
    return () => { delete document.documentElement.dataset.module; };
  }, [cle]);
  return null;
}

function Contenu({ module, view, onNav, onClose, compact }) {
  const go = (route) => { onNav(route); if (onClose) onClose(); };
  const items = (module.items || []).filter(i => i.show !== false);
  const grouped = items.some(i => i.group);
  const groupes = grouped ? [...new Set(items.map(i => i.group || "Autres"))] : [];
  const groupeColor = (g, idx) => (items.find(i => i.group === g && i.groupColor)?.groupColor) || PALETTE[idx % PALETTE.length];
  let colorIdx = 0;
  const nextColor = () => PALETTE[(colorIdx++) % PALETTE.length];

  // Hiérarchie menu / sous-menu (demande Pierre 22/08/2026) : un sous-menu est
  // un bloc DÉPLIABLE sous son parent — chevron + compteur sur le parent, rail
  // et fond teintés à la couleur du parent, lignes plus petites et plus
  // claires. Ouvert d'office quand la page active est le parent ou l'un de
  // ses enfants ; l'utilisateur peut déplier/replier les autres.
  const parents = [module.accueil, ...items].filter(Boolean);
  const parentDe = (r) => parents.find(p => p.route === r || (p.children || []).some(c => c.route === r))?.route || null;
  const [ouverts, setOuverts] = useState(() => new Set([parentDe(view)].filter(Boolean)));
  useEffect(() => {
    const p = parentDe(view);
    if (p) setOuverts(prev => prev.has(p) ? prev : new Set([...prev, p]));
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (route) => setOuverts(prev => {
    const n = new Set(prev); if (n.has(route)) n.delete(route); else n.add(route); return n;
  });

  // En mode `compact` (tiroir mobile), le doigt remplace la souris : lignes et
  // chevron au plancher Apple de 44 pt, libellé sur deux lignes plutôt que coupé
  // à l'ellipse (il ne restait que ~212 px pour « Registre des organismes
  // d'assurance »), et la `description` de navConstants — jusqu'ici portée par le
  // seul attribut `title`, qui ne s'ouvre qu'au survol souris, donc JAMAIS lue
  // sur iPhone — rendue sous le libellé.
  const Ligne = ({ route, icon, label, description, color, enfant = false, nbEnfants = 0, ouvert = false, compteur, reserve = false }) => {
    const actif = view === route;
    const c = color || NAVY;
    const pastille = enfant ? (compact ? 22 : 18) : 26;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: compact ? 6 : 2 }}>
        <button className="amsb-item" onClick={() => go(route)} title={description ? label + " : " + description : label}
          style={{
            display: "flex", alignItems: "center", gap: enfant ? 8 : 10, flex: 1, minWidth: 0,
            background: actif ? alpha(c, .11) : "transparent",
            border: "none", borderRadius: 9,
            padding: compact ? (enfant ? "10px 10px" : "11px 10px") : (enfant ? "4px 8px" : "7px 10px"),
            minHeight: compact ? 44 : undefined,
            cursor: "pointer", textAlign: "left", transition: "background .15s ease",
          }}>
          <Tuile couleur={c} taille={pastille} icone={icon || "chevron-right"}
            variante={actif ? "pleine" : "lavis"} />
          <span style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
            <span style={{
              fontSize: enfant ? "calc(11.5px * var(--fs-texte, 1))" : "calc(12.5px * var(--fs-texte, 1))", fontWeight: actif ? 600 : 500,
              color: actif ? navTexte(c) : (enfant ? "var(--text-sub)" : "var(--text-main)"),
              overflow: "hidden", textOverflow: "ellipsis",
              ...(compact
                ? { whiteSpace: "normal", lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }
                : { whiteSpace: "nowrap" }),
            }}>{label}{reserve && <CadenasAdmin />}</span>
            {compact && !enfant && description && (
              <span style={{
                fontSize: "calc(11px * var(--fs-texte, 1))", lineHeight: 1.3, color: "var(--text-sub)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{description}</span>
            )}
          </span>
          <PastilleCompteur compte={compteEntree({ route, compteur })} />
        </button>
        {nbEnfants > 0 && (
          <button onClick={() => toggle(route)} aria-expanded={ouvert}
            title={ouvert ? "Replier le sous-menu" : `Déplier le sous-menu (${nbEnfants})`}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              background: ouvert ? "transparent" : alpha(c, .078),
              border: "none", borderRadius: compact ? 12 : 8,
              padding: compact ? "0 8px" : "4px 6px",
              minWidth: compact ? 44 : undefined, minHeight: compact ? 44 : undefined,
              flexShrink: 0, cursor: "pointer",
              color: ouvert ? "var(--text-sub)" : navTexte(c), fontSize: compact ? "var(--fs-nav)" : "calc(10.5px * var(--fs-texte, 1))", fontWeight: 700,
            }}>
            {!ouvert && <span>{nbEnfants}</span>}
            {/* Au bureau, le tracé Tabler n'occupe que le tiers central de sa
                grille : la marge négative rend à la ligne la largeur de l'ancien
                « › » (sinon « Conseil d'administration » se coupait en 1440 px). */}
            <Icone name="chevron-right" size={compact ? 16 : 12} style={{ transition: "transform .18s ease", transform: ouvert ? "rotate(90deg)" : "none", margin: compact ? 0 : "0 -3px" }} />
          </button>
        )}
      </div>
    );
  };
  const renderItem = (it, parentColor) => {
    const c = it.color || parentColor || nextColor();
    const kids = (it.children || []).filter(ch => ch.show !== false);
    const ouvert = ouverts.has(it.route);
    return (
      <div key={it.route}>
        <Ligne route={it.route} icon={it.icon} label={it.label} description={it.description} color={c}
          compteur={it.compteur} nbEnfants={kids.length} ouvert={ouvert} reserve={!!it.reserveAdmin} />
        {kids.length > 0 && ouvert && (
          <div style={{
            marginLeft: 14, marginTop: 2, marginBottom: 6, padding: "3px 3px 3px 6px",
            borderLeft: `2px solid ${alpha(c, .4)}`, background: alpha(c, .043), borderRadius: "0 10px 10px 0",
            // Le `gap` du <nav> ne descend pas jusqu'ici : sans cette pile, les
            // enfants (5 écrans Budget, 9 pages Hygie) se touchaient au pixel.
            // Tiroir uniquement — le rail du bureau garde son empilement en bloc.
            ...(compact ? { display: "flex", flexDirection: "column", gap: 4 } : null),
          }}>
            {kids.map(ch => (
              <Ligne key={ch.route} route={ch.route} icon={ch.icon} label={ch.label} description={ch.description}
                color={ch.color || c} compteur={ch.compteur} enfant reserve={!!ch.reserveAdmin} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav style={{
      display: "flex", flexDirection: "column", gap: compact ? 4 : 2,
      // Tiroir : la dernière entrée (Pilotage en compte 14 avec Budget déplié)
      // tombait sous la barre d'outils Safari / la barre d'accueil. --sab et
      // --sal viennent du SOCLE MOBILE (App.jsx) et valent 0 hors iPhone.
      padding: compact
        ? "10px 10px calc(20px + var(--sab, 0px)) calc(10px + var(--sal, 0px))"
        : "12px 10px 14px",
    }}>
      {/* Le survol reste souris : sur iPhone l'état :hover se COLLE après le tap
          (la ligne restait grisée au retour dans le tiroir). Au doigt, c'est
          l'appui (:active) qui donne le retour. */}
      <style>{`@media (hover: hover) and (pointer: fine) { .amsb-item:hover { background: color-mix(in srgb, var(--text-main) 7%, transparent) !important; } }
@media (pointer: coarse) { .amsb-item:active { background: color-mix(in srgb, var(--text-main) 12%, transparent) !important; } }`}</style>
      <div className={compact ? "enio-verre" : undefined} style={{
        display: "flex", alignItems: "center", gap: 6,
        color: navTexte(module.color || NAVY), fontFamily: "Montserrat,sans-serif",
        margin: compact ? "0 0 6px" : "2px 10px 8px",
        // En tiroir, l'en-tête reste collé en haut : la croix de fermeture doit
        // rester atteignable même après avoir fait défiler les 14 entrées.
        ...(compact ? {
          position: "sticky", top: 0, zIndex: 2, borderRadius: 12,
          padding: "6px 4px 6px 10px",
          background: "color-mix(in srgb, var(--bg-card) 88%, transparent)",
          backdropFilter: "blur(16px) saturate(1.6)", WebkitBackdropFilter: "blur(16px) saturate(1.6)",
        } : null),
      }}>
        <span style={{
          flex: 1, minWidth: 0, fontSize: "var(--fs-nav)", fontWeight: 600,
          // L'ellipse ne sert qu'au tiroir, où la croix de fermeture partage la
          // ligne ; sur le rail du bureau, le libellé se comporte comme avant.
          ...(compact ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : null),
        }}>{module.label}</span>
        {/* Sur iPhone SE il ne restait que ~45 px de voile à droite pour refermer
            le tiroir : une croix explicite évite de viser cette bande. */}
        {compact && onClose && (
          <button onClick={onClose} title="Fermer le menu" aria-label="Fermer le menu"
            style={{
              width: 44, height: 44, minWidth: "var(--cible-min)", flexShrink: 0,
              display: "grid", placeItems: "center", cursor: "pointer",
              background: "color-mix(in srgb, var(--text-main) 8%, transparent)",
              border: "none", borderRadius: 12, color: "var(--text-sub)", fontSize: "calc(16px * var(--fs-texte, 1))",
            }}><Icone name="x" size={18} /></button>
        )}
      </div>
      {module.accueil && renderItem({ ...module.accueil, label: /^Accueil\b/i.test(module.accueil.label || "") ? "Accueil" : module.accueil.label, color: module.accueil.color || module.color || NAVY })}
      {grouped ? groupes.map((g, gi) => (
        <div key={g}>
          <div style={{
            fontSize: "var(--fs-micro)", fontWeight: 600,
            color: navTexte(groupeColor(g, gi)), margin: "14px 10px 5px",
          }}>{g}</div>
          {items.filter(i => (i.group || "Autres") === g).map(it => renderItem(it))}
        </div>
      )) : (
        <div style={{ marginTop: module.accueil ? 6 : 0 }}>
          {items.map(it => renderItem(it))}
        </div>
      )}
    </nav>
  );
}

// `tiroir` (lot L6) : bouton « Sommaire » et tiroir même au clavier-souris —
// le bureau de moins de 1 024 px (rail en icônes seules) et la fiche ouverte.
// Au bureau large, le sommaire vit dans le rail (RailNavigation.js) et App ne
// monte plus ce composant.
export function ModuleSidebar({ module, view, onNav, tiroir = false }) {
  // Plus AUCUNE lecture de largeur ici — et c'est tout l'objet du changement.
  // L'ancienne sonde `window.innerWidth < 1000` refermait la boucle décrite
  // dans src/coquilleMobile.test.js : quand iOS Safari élargissait le viewport
  // de mise en page pour absorber un débordement, innerWidth franchissait 1000
  // et ce rail de 270 px INCOMPRESSIBLES se rallumait sur un téléphone, ce qui
  // élargissait la page davantage. Le prédicat, lui, lit le pointeur et l'écran
  // physique : l'élargissement ne peut plus le tromper.
  const { estBureau } = useCoquille();
  const [ouvert, setOuvert] = useState(false);
  if (!module) return null;
  const mobile = tiroir || !estBureau;
  const verre = {
    background: "color-mix(in srgb, var(--bg-card) 80%, transparent)",
    backdropFilter: "blur(22px) saturate(1.6)",
    WebkitBackdropFilter: "blur(22px) saturate(1.6)",
    border: "1px solid var(--border-color)",
    boxShadow: "0 10px 34px rgba(0,0,0,.09)",
  };
  if (mobile) {
    const tous = [module.accueil, ...(module.accueil?.children || []), ...(module.items || []),
      ...(module.items || []).flatMap(i => i.children || [])].filter(Boolean);
    const actuel = tous.find(i => i.route === view) || { icon: module.icon, label: module.label };
    // Cadenas des pages réservées aux administrateurs (14/10/2026) : l'entrée
    // qui sert la vue (une sous-page suit la sienne), ou le module entier.
    const servie = tous.find(i => i.route === pageServie(view));
    const reserve = !!(module.reserveAdmin || (servie && servie.reserveAdmin) || actuel.reserveAdmin);
    // L'écho du module à droite se tait quand la page le nomme déjà
    // (« Accueil Ressources humaines » suivi de « Ressources humaines › » ne
    // tenait pas à 375 px et tronquait la page : regard de la vitrine d'accès,
    // lot L11t). Le libellé de la page garde la priorité ; libellés inchangés.
    const echo = !!module.label && String(actuel.label || "").includes(module.label);
    return (
      <>
        <button className="amsb-ouvrir enio-verre" onClick={() => setOuvert(true)}
          aria-label={"Sommaire du module " + module.label + ", page : " + (actuel.label || module.label)}
          style={{
          ...verre, display: "flex", alignItems: "center", gap: 8,
          flex: "1 1 100%", width: "100%",
          borderRadius: 12, padding: "12px 14px", minHeight: "var(--cible-min)", marginBottom: 2,
          fontSize: "calc(13.5px * var(--fs-texte, 1))", fontWeight: 700, color: "var(--text-main)", cursor: "pointer",
          // Ce bouton est le SEUL chemin vers les autres sous-pages en téléphone :
          // posé dans le flux, il fallait remonter tout en haut d'une page longue
          // (Registre du personnel, Séances d'une instance) pour changer d'écran.
          // Collé sous le header comme son équivalent desktop (rail sticky).
          position: "sticky", top: "calc(var(--app-header-h, 56px) + 4px)", zIndex: 900,
        }}>
          <Icone name="menu-2" size={18} />
          {actuel.icon && <Icone name={actuel.icon} size={18} style={{ color: navTexte(module.color || NAVY) }} />}
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {actuel.label}
          </span>
          {reserve && <CadenasAdmin />}
          <span style={{ marginLeft: "auto", flexShrink: echo ? 0 : 1, minWidth: 0, maxWidth: "45%", color: "var(--text-sub)", fontSize: "calc(11px * var(--fs-texte, 1))",
            display: "inline-flex", alignItems: "center", gap: 2 }}>
            {!echo && <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{module.label}</span>}
            <Icone name="chevron-right" size={12} />
          </span>
        </button>
        {ouvert && (
          <div className="enio-voile" onClick={() => setOuvert(false)} style={{
            // Sous le header, comme les modales (convention --app-header-h) : le
            // bandeau de modules reste visible et touchable, et c'est LUI qui
            // porte déjà le décalage d'encoche — le tiroir n'a plus à le refaire.
            position: "fixed", top: "var(--app-header-h, 56px)", left: 0, right: 0, bottom: 0, zIndex: 10000,
            background: "rgba(0,0,0,.32)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
          }}>
            <div className="enio-verre" onClick={e => e.stopPropagation()} style={{
              ...verre, position: "absolute", top: 0, left: 0, height: "100%",
              // 300 px fixes rognaient les libellés longs et ne laissaient que
              // 75 px de voile sur iPhone SE ; on suit la largeur de l'écran.
              width: "min(var(--vw100) * 0.88, 340px)",
              overflowY: "auto", WebkitOverflowScrolling: "touch",
              // Sans confinement, arrivé en bas de la liste le geste continuait
              // et faisait défiler la page DERRIÈRE le voile.
              overscrollBehavior: "contain",
              borderRadius: "0 18px 18px 0",
              animation: "amsbSlide .22s cubic-bezier(.32,.72,.35,1)",
            }}>
              <style>{`@keyframes amsbSlide { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
              <Contenu module={module} view={view} onNav={onNav} onClose={() => setOuvert(false)} compact />
            </div>
          </div>
        )}
      </>
    );
  }
  return (
    <aside className="enio-verre" style={{
      ...verre, width: 270, minWidth: 270, borderRadius: 18,
      position: "sticky", top: "calc(var(--app-header-h, 56px) + 12px)",
      maxHeight: "calc(var(--vh100) - var(--app-header-h, 56px) - 26px)",
      overflowY: "auto", alignSelf: "flex-start",
    }}>
      <Contenu module={module} view={view} onNav={onNav} />
    </aside>
  );
}
